import type {
  FieldValue,
  RelationshipSummaryValue,
  RelationshipValue,
  TeamSelectorValue,
} from '@kizen/api';
import type { Country } from '@kizen/api/geography';
import type { CustomObjectField } from '@kizen/api/custom-object';
import type { TimezoneName } from 'countries-and-timezones';
import { format } from 'date-fns/format';
import { formatInTimeZone } from 'date-fns-tz';
import get from 'lodash/get';
import parsePhoneNumber from 'libphonenumber-js/max';
import { isTimezoneEqualToLocal } from 'app/timezone';
import { BusinessVariable } from '__components/WYSIWYG/use-merge-fields';

/**
 * Flattens arrays types out of a union yielding their type along
 * with all other non-array types. Recursively flattens array types
 * and dedupes the final result.
 *
 * Useful when a type looks like `T | T[]` and you want `T`.
 *
 * @example
 * ```ts
 * // T | T[] --> T
 * type Union = { name: string } | { name: string }[];
 * type Flattened = FlattenUnion<Union>; // { name: string }
 * ```
 * @example
 * ```ts
 * // A | A[] | B[] --> A | B
 * type Union =
 *  | { name: string }
 *  | { name: string }[]
 *  | { id: string }[];
 * type Flattened = FlattenUnion<Union>; // { name: string } | { id: string }
 * ```
 * @example
 * ```ts
 * // A | B[] | C[][] --> A | B | C
 *  * type Union =
 *  | { a: string }
 *  | { b: string }[]
 *  | { c: string }[][];
 * type Flattened = FlattenUnion<Union>; // { a: string } | { b: string } | { c: string }
 * ```
 */
type FlattenUnion<T, Original = T> = T extends any[]
  ? MaybeAdd<WithoutArrays<Original>, FlattenUnion<T[number]>>
  : T;
type WithoutArrays<T> = T extends any[] ? never : T;
type MaybeAdd<A, B> = B extends A ? A : A | B;

export type FieldValueWithRelations =
  | Exclude<FieldValue, RelationshipValue>
  | RelationshipValueWithRelation;

export type RelationshipValueWithRelation = RelationshipValue & {
  relation: CustomObjectField['relation'];
};

type BusinessLike = {
  city: string;
  country: string;
  name: string;
  phone: string;
  phone_default_region: string;
  postal_code: string;
  street_address: string;
  reply_to_email: string;
  state: string;
  timezone: { name: TimezoneName };
  primary_marketing_contact_name: string;
  primary_marketing_contact_email: string;
};

type PhoneRegion = {
  value: string;
  label: string;
};

type GetMergeFieldLookupOptions = {
  business: BusinessLike;
  countries?: Country[];
  phoneRegions?: PhoneRegion[];
};

type GetTeamMemberMergeFieldLookup = {
  teamMember: {
    email: string;
    first_name: string;
    last_name: string;
    phone: string;
  };
  roles?: string[];
  phoneRegions?: PhoneRegion[];
  business: BusinessLike;
};

const getRelationshipElement = (
  value: FlattenUnion<
    Exclude<RelationshipValue, RelationshipSummaryValue>['value']
  >,
  relation: CustomObjectField['relation']
) => {
  const link = getRelationLink(value.id, relation);

  if (isTeamSelectorValue(value)) {
    let display = `${value.first_name} ${value.last_name}`.trim();

    if (value.email) {
      display = display ? `${display} (${value.email})` : value.email;
    }

    return `<a target="_blank" href="${link}">${display}</a>`;
  }

  return `<a target="_blank" href="${link}">${value.display_name}</a>`;
};

const isTeamSelectorValue = (
  value: any
): value is TeamSelectorValue['value'] => {
  if (value && typeof value === 'object') {
    return (
      typeof value.first_name === 'string' &&
      typeof value.last_name === 'string' &&
      typeof value.email === 'string'
    );
  }
  return false;
};

const getCodeFromRegion = (phoneRegion?: PhoneRegion): string => {
  if (!phoneRegion) return '';
  const matches = phoneRegion.label.match(/.*\((.*)\)/); // ex. United States (+1)
  return matches?.pop() ?? '';
};

const getRelationLink = (
  fieldId: string,
  relation: CustomObjectField['relation']
) => {
  if (relation.fetch_url === 'client') {
    return `/client/${fieldId}/details`;
  }

  return `/custom-objects/${relation.related_object}/${fieldId}/details`;
};

const getMergeFieldValue = (
  fv: FieldValueWithRelations,
  t: (x: string) => string
): string => {
  if ('value_summary' in fv) {
    return `${fv.value_summary.count} ${t('records')}`;
  }

  if (fv.value === undefined || fv.value === null) {
    return '';
  }

  switch (fv.field_type) {
    case 'checkbox':
      return fv.value ? t('Is Checked') : t('Is Not Checked');
    case 'checkboxes': {
      const checked = fv.value.map((option) => option.name);
      return checked.join(', ');
    }
    case 'date':
      if (!fv.value) {
        return '';
      }
      return format(fv.value, 'MMMM dd, yyyy');
    case 'datetime':
      if (!fv.value) {
        return '';
      }
      return format(fv.value, 'MMMM dd, yyyy h:mm a');
    case 'decimal':
      return fv.value.toString();
    case 'dropdown':
      return fv.value.name;
    case 'dynamictags':
      return fv.value.map((option) => option.name).join(', ');
    case 'files':
      return fv.value.map((file) => file.name).join(', ');
    case 'integer':
      return fv.value.toString();
    case 'money':
      if (!fv.value) {
        return '';
      }
      return `${fv.value.symbol}${fv.value.amount}`;
    case 'phonenumber':
      try {
        const parsedPhone = parsePhoneNumber(fv.value.e164);
        if (parsedPhone) {
          return parsedPhone.formatInternational();
        }
        return fv.value.e164;
      } catch (err) {
        return '';
      }
    case 'radio':
      return fv.value.name;
    case 'rating':
      return fv.value.toString();
    case 'relationship': {
      if (Array.isArray(fv.value)) {
        return fv.value
          .map((x) => getRelationshipElement(x, fv.relation))
          .join(', ');
      }

      return getRelationshipElement(fv.value, fv.relation);
    }
    case 'status':
      return fv.value.name;
    case 'team_selector': {
      if (fv.value.first_name || fv.value.last_name) {
        const name =
          `${fv.value.first_name ?? ''} ${fv.value.last_name ?? ''}`.trim();

        if (fv.value.email) {
          return `${name} (${fv.value.email})`;
        }

        return name;
      }

      return fv.value.email ?? '';
    }
    case 'timezone':
      return fv.value.name;
    case 'yesnomaybe':
      return fv.value.name;
    case 'email':
    case 'longtext':
    case 'text':
      return fv.value;
  }
};

/**
 * Business merge fields look like `{{ business.name }}`. This utility should be used to create
 * an object such that the `business.name` path lookup resolves when using lodash/get.
 *
 * ```ts
 * const lookup = {
 *  business: getBusinessMergeFieldLookup(...)
 * }
 * ```
 *
 * @param param0
 * @returns
 */
export const getBusinessMergeFieldLookup = ({
  business,
  countries,
  phoneRegions,
}: GetMergeFieldLookupOptions) => {
  const now = new Date();
  const businessTimezone = business.timezone.name;
  const businessDate = now.toLocaleString('en-US', {
    timeZone: businessTimezone,
  });
  const defaultCallingCode = getCodeFromRegion(
    phoneRegions?.find((x) => x.value === business.phone_default_region)
  );
  let businessTime = format(businessDate, 'h:mm a');
  if (!isTimezoneEqualToLocal(businessTimezone)) {
    const offset = formatInTimeZone(now, businessTimezone, 'XXX');
    businessTime += ` ${businessTimezone} (GMT${offset})`;
  }

  let businessPhone = business.phone;
  if (businessPhone) {
    const parsedPhone = parsePhoneNumber(business.phone);
    if (parsedPhone) {
      businessPhone =
        defaultCallingCode && business.phone.startsWith(defaultCallingCode)
          ? parsedPhone.formatNational()
          : parsedPhone.formatInternational();
    }
  }

  const country = countries?.find((c) => c.id === business.country);
  let state;
  if (country) {
    state = country.subdivision_groups
      .flatMap((x) => x.subdivisions)
      .find((y) => y.id === `${country.id}-${business.state}`);
  }

  return {
    city: business.city,
    reply_to_email: `<a href="mailto:${business.reply_to_email}">${business.reply_to_email}</a>`,
    country: country?.name ?? business.country,
    country_code: country?.id ?? business.country,
    name: business.name,
    phone: businessPhone,
    postal_code: business.postal_code,
    street_address: business.street_address,
    state: state?.name ?? business.state,
    state_code: business?.state,
    time: businessTime,
    primary_marketing_contact_name: business.primary_marketing_contact_name,
    primary_marketing_contact_email: business.primary_marketing_contact_email,
  };
};

/**
 * Custom Business Variables merge fields look like `{{ business_variable.api_name }}`. This utility should be used to create
 * an object such that the `business_variable.api_name` path lookup resolves when using lodash/get.
 *
 * ```ts
 * const lookup = {
 *  business_variable: getBusinessVariablesMergeFieldLookup(
 *   chosenBusiness.business_variables
 *  )
 * }
 * ```
 *
 * @param param0
 * @returns
 */
export const getBusinessVariablesMergeFieldLookup = (
  businessVariables: BusinessVariable[] = []
) => {
  return businessVariables.reduce<Record<string, string>>(
    (acc, { api_name, value_string }) => {
      if (api_name) {
        acc[api_name] = value_string;
      }
      return acc;
    },
    {}
  );
};

/**
 * Team member merge fields look like `{{ team_member.email }}`. This utility should be used to create
 * an object such that the `team_member.email` path lookup resolves when using lodash/get.
 *
 * ```ts
 * const lookup = {
 *  team_member: getTeamMemberMergeFieldLookup(...)
 * }
 * ```
 *
 * @param param0
 * @returns
 */
export const getTeamMemberMergeFieldLookup = ({
  teamMember,
  roles,
  phoneRegions,
  business,
}: GetTeamMemberMergeFieldLookup) => {
  const now = new Date();
  let teamMemberPhone = teamMember?.phone;
  const defaultCallingCode = getCodeFromRegion(
    phoneRegions?.find((x) => x.value === business.phone_default_region)
  );
  if (teamMemberPhone) {
    const parsedPhone = parsePhoneNumber(teamMemberPhone);
    if (parsedPhone) {
      teamMemberPhone =
        defaultCallingCode && teamMember.phone.startsWith(defaultCallingCode)
          ? parsedPhone.formatNational()
          : parsedPhone.formatInternational();
    }
  }

  return {
    email: `<a href="mailto:${teamMember.email}">${teamMember.email}</a>`,
    first_name: teamMember.first_name,
    last_name: teamMember.last_name,
    roles: roles?.join(', ') ?? '',
    phone: teamMemberPhone,
    date: format(now, 'MMMM dd, yyyy'),
    time: format(now, 'h:mm a'),
  };
};

export const getMergeFieldLookupForFields = (
  fields: FieldValueWithRelations[],
  t: (x: string) => string
): Record<string, string> => {
  const lookup = fields.reduce<Record<string, string>>((acc, field) => {
    acc[field.name as string] = getMergeFieldValue(field, t);
    return acc;
  }, {});

  return lookup;
};

/**
 * Example merge fields
 * - `{{ business.name }}`
 * - `{{ team_member.first_name }}`
 * - `{{ my_object_dk32eds.text_dk3kf }}`
 */
export const replaceMergeFields = (
  content: string,
  lookup: Record<string, Record<string, string>>
) => {
  return content.replace(/{{\s*([0-9A-Za-z._-]+)\s*}}/g, (_match, path) => {
    const value = get(lookup, path, '');
    return typeof value === 'string' ? value : '';
  });
};

/**
 * @example
 * ```ts
 * const content = '{{ team_member.first_name }} is a part of {{ business.name }} business.':
 * const mergeFields = parseMergeFields(content);
 * // ['team_member.first_name', 'business.name'];
 * ```
 */
export const parseMergeFields = (content: string): string[] => {
  const set = new Set<string>();

  for (const match of content.matchAll(/{{\s*([0-9A-Za-z._-]+)\s*}}/g)) {
    set.add(match[1]);
  }

  return Array.from(set);
};
